
import getEnvironment from '../../../env';
import { RequestMutationWrapper } from '../../../models/API/RequestMutationWrapper';
import BaseModel from '../../../models/BaseModels/BaseModel';
import ServiceRequest, { CreateServiceRequest, MoveServiceRequest, ServiceResponse, ServiceResponseDetails, SimpleServiceResponse } from '../../../models/Service';
import HandleQuery from '../../../redux/EndpointQueryHandler';
import { GetManyPackage } from '../../../redux/GetManyPackage';
import handleResponse from '../../../redux/handleResponse';
import { portalApiSlice } from '../../../redux/portalApiSlice';
import { transformMultipleResponseToRightType, transformSingleResponseToRightType } from '../../../redux/transformResponse';

const baseurl = getEnvironment().REACT_APP_API_BASEURL;

export const serviceApiSlice = portalApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllServices: builder.query<SimpleServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: SimpleServiceResponse[], b, c) => transformMultipleResponseToRightType(SimpleServiceResponse, a, b, c),
      providesTags: ['services']
    }),
    getServiceById: builder.query<ServiceResponseDetails, string>({
      query: (uuid) => HandleQuery({
        url: `${baseurl}/services/${uuid}`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Service could not be fetched" }),
      }),
      transformResponse: (a: ServiceResponseDetails, b, c) => transformSingleResponseToRightType(ServiceResponseDetails, a, b, c),
      providesTags: ['service']
    }),
    getServicesByOrganisationId: builder.query<SimpleServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/organisations/${pack.uuid}/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: SimpleServiceResponse[], b, c) => transformMultipleResponseToRightType(SimpleServiceResponse, a, b, c),
      providesTags: ['services']
    }),
    getServicesByOrganisationAndApprover: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/organisations/${pack.uuid}/service-approver/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services']
    }),
    getServicesByApprover: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/users/${pack.uuid}/service-approver/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services', 'service-approvers']
    }),
    getServicesByUserContact: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/users/${pack.uuid}/contact/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services', 'service-contacts']
    }),
    getServicesByExternalContactContact: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/external-contacts/${pack.uuid}/contact/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services', 'service-contacts']
    }),
    updateService: builder.mutation<ServiceResponseDetails, RequestMutationWrapper<ServiceRequest>>({
      query: (request) => HandleQuery({
        url: `${baseurl}/services/${request.data.uuid}`,
        responseHandler: (res) => handleResponse({ response: res, toastSuccessText: "Service was updated!", toastErrorText: "Service could not be updated" }),
        method: "PATCH",
        body: {
          name: request.data.name,
          criticality: request.data.criticality,
          propagation: request.data.propagation,
          requires_approval: request.data.requires_approval,
          uses_encryption: request.data.uses_encryption,
          no_encryption_reason: request.data.no_encryption_reason,
          service_categories: request.data.service_categories,
          sla: request.data.sla,
          sla_description: request.data.sla_description,
          service_window_rules: request.data.service_window_rules,
          general_contact_information: request.data.general_contact_information,
          monitoring: {
            tcp_portnr: request.data.monitoring.tcp_portnr,
            http_url: request.data.monitoring.http_url,
            icmp_ping: request.data.monitoring.icmp_ping,
            tcp_ping: request.data.monitoring.tcp_ping,
            http_ping: request.data.monitoring.http_ping,
            alarming: request.data.monitoring.alarming,
            email: request.data.monitoring.email
          },
          internal_description: request.data.internal_description,
          description: request.data.description,
          servicetag_uuid: request.data.servicetag ? request.data.servicetag?.uuid : "00000000-0000-0000-0000-000000000000",

        }
      }),
      transformResponse: (a: ServiceResponseDetails, b, c) => transformSingleResponseToRightType(ServiceResponseDetails, a, b, c),
      invalidatesTags: ['services', 'service']
    }),
    changeServiceHostingsite: builder.mutation<MoveServiceRequest, RequestMutationWrapper<MoveServiceRequest>>({
      query: (request) => HandleQuery({
        url: `${baseurl}/services/${request.data.service?.uuid}/move`,
        responseHandler: (res) => handleResponse({ response: res, toastSuccessText: "Service was updated!", toastErrorText: "Service could not be updated" }),
        method: "PATCH",
        body: {
          organisation_uuid: request.data.owner_organisation?.uuid,
          hosting_site_uuid: request.data.hosting_site?.uuid,
          service_tag_uuid: request.data.servicetag?.uuid,
          endpoints: request.data.endpoints?.map(endpoint => ({
            uuid: endpoint.uuid,
            ip: endpoint.ip,
            dns_name: endpoint.dns_name,
            subnet_uuid: endpoint.subnet?.uuid,
            dns_suffix_uuid: endpoint.dns_suffix?.uuid,
            portalias_uuids: endpoint.ports.map(port => port.uuid)
          }))
        }
      }),
      transformResponse: (a: ServiceResponseDetails, b, c) => transformSingleResponseToRightType(ServiceResponseDetails, a, b, c),
      invalidatesTags: ["service", "services", "hosting-sites", "users", "endpoints", "service-contacts", "service-approvers"]
    }),
    addService: builder.mutation<BaseModel, RequestMutationWrapper<CreateServiceRequest>>({
      query: (request) => HandleQuery({
        url: `${baseurl}/hosting-sites/${request.data.hosting_site?.uuid}/services`,
        responseHandler: (res) => handleResponse({ response: res, toastSuccessText: "Service was created", toastErrorText: "Service could not be created" }),
        method: 'POST',
        body: {
          ...request.data,
          servicetag_uuid: request.data.servicetag?.uuid,
          endpoints: request.data.endpoints?.map(e => ({
            subnet_uuid: e.subnet?.uuid,
            dns_suffix_uuid: e.dns_suffix?.uuid,
            portalias_uuids: e.ports.map(p => p.uuid),
            ip: e.ip,
            dns_name: e.dns_name
          })),
          service_approvers: request.data.approvers?.map(approver => approver.uuid),
          monitoring: {
            ...request.data.monitoring,
            tcp_portnr: request.data.monitoring.tcp_portnr
          },
          contacts: request.data.contacts.map(contact => ({
            external_contact_uuid: contact.contact?.user_uuid == undefined ? contact.contact?.uuid : undefined,
            user_uuid: contact.contact?.user_uuid,
            contact_label_uuids: contact.labels?.map(label => label.uuid) ?? []
          }))
        },
      }),
      transformResponse: (a: ServiceResponse, b, c) => transformSingleResponseToRightType(ServiceResponse, a, b, c),
      // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created post could show up in any lists.
      invalidatesTags: ['services', 'service'],
    }),
    forceAddService: builder.mutation<BaseModel, RequestMutationWrapper<CreateServiceRequest>>({
      query: (request) => HandleQuery({
        url: `${baseurl}/hosting-sites/${request.data.hosting_site?.uuid}/services/force`,
        responseHandler: (res) => handleResponse({ response: res, toastSuccessText: "Service was created", toastErrorText: "Service could not be created" }),
        method: 'POST',
        body: {
          ...request.data,
          servicetag_uuid: request.data.servicetag?.uuid,
          endpoints: request.data.endpoints?.map(e => ({
            subnet_uuid: e.subnet?.uuid,
            dns_suffix_uuid: e.dns_suffix?.uuid,
            portalias_uuids: e.ports.map(p => p.uuid),
            ip: e.ip,
            dns_name: e.dns_name
          })),
          service_approvers: request.data.approvers?.map(approver => approver.uuid),
          monitoring: {
            ...request.data.monitoring,
            tcp_portnr: request.data.monitoring.tcp_portnr
          },
          contacts: request.data.contacts.map(contact => ({
            external_contact_uuid: contact.contact?.user_uuid == undefined ? contact.contact?.uuid : undefined,
            user_uuid: contact.contact?.user_uuid,
            contact_label_uuids: contact.labels?.map(label => label.uuid) ?? []
          }))
        },
      }),
      transformResponse: (a: ServiceResponse, b, c) => transformSingleResponseToRightType(ServiceResponse, a, b, c),
      // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created post could show up in any lists.
      invalidatesTags: ['services', 'service'],
    }),
    getServicesByHostingSiteId: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/hosting-sites/${pack.uuid}/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services']
    }),
    getServicesByPortaliasId: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/portaliases/${pack.uuid}/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services']
    }),
    deleteService: builder.mutation<void, RequestMutationWrapper<ServiceRequest>>({
      query: (service) => HandleQuery({
        url: `${baseurl}/services/${service.data.uuid}`,
        method: 'DELETE',
        responseHandler: (res) => handleResponse({ response: res, toastSuccessText: "Service was deleted!", toastErrorText: "Service could not be deleted" }),
      }),
      invalidatesTags: ['services']
    }),
    deleteServiceByUuid: builder.mutation<void, RequestMutationWrapper<string>>({
      query: (service) => HandleQuery({
        url: `${baseurl}/services/${service.data}`,
        method: 'DELETE',
        responseHandler: (res) => handleResponse({ response: res, toastSuccessText: "Service was deleted!", toastErrorText: "Service could not be deleted" }),
      }),
      invalidatesTags: ['services']
    }),
    getServicesByServiceWindowId: builder.query<ServiceResponse[], GetManyPackage>({
      query: (pack) => HandleQuery({
        url: `${baseurl}/service-windows/${pack.uuid}/services`,
        responseHandler: (res) => handleResponse({ response: res, toastWithResult: false, toastErrorText: "Services could not be fetched" }),
        pagination: pack.pagination
      }),
      transformResponse: (a: ServiceResponse[], b, c) => transformMultipleResponseToRightType(ServiceResponse, a, b, c),
      providesTags: ['services']
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetAllServicesQuery, useGetServiceByIdQuery, useChangeServiceHostingsiteMutation, useDeleteServiceMutation, useDeleteServiceByUuidMutation, useUpdateServiceMutation, useGetServicesByOrganisationIdQuery, useAddServiceMutation, useForceAddServiceMutation, useGetServicesByHostingSiteIdQuery, useGetServicesByPortaliasIdQuery, useGetServicesByServiceWindowIdQuery, useGetServicesByOrganisationAndApproverQuery, useGetServicesByApproverQuery, useGetServicesByUserContactQuery, useGetServicesByExternalContactContactQuery } = serviceApiSlice
